import { useEffect, useRef, useState } from "react";
import { useImportCustomers, useImportJobStatus, useDownloadImportTemplate } from "../hooks/useCustomer";
import { useNotification } from "@/shared/hooks/useNotification";
import { getErrorMessage } from "@/shared/utils/errorHandler";
import { queryClient } from "@/shared/services/queryClient";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Upload, Download, Loader2 } from "lucide-react";
import { AlertCircle, CheckCircle2, AlertTriangle } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";

export const BulkImportDialog = () => {
  const [open, setOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [jobId, setJobId] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const notifiedRef = useRef(false);

  const importMutation = useImportCustomers();
  const downloadTemplateMutation = useDownloadImportTemplate();
  const { data: jobStatus } = useImportJobStatus(jobId);
  const notify = useNotification();

  const isProcessing = jobStatus?.status === 'Pending' || jobStatus?.status === 'Processing';
  const isTerminal = jobStatus?.status === 'Completed' || jobStatus?.status === 'Failed';

  // Notify once when the job reaches a terminal state
  useEffect(() => {
    if (!jobStatus || notifiedRef.current) return;
    if (jobStatus.status === 'Completed') {
      notifiedRef.current = true;
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      if (jobStatus.failureCount === 0) {
        notify.success(`Import complete! ${jobStatus.successCount} customers imported.`);
      } else {
        notify.warning(
          `Import done: ${jobStatus.successCount} imported, ${jobStatus.failureCount} skipped. See details below.`
        );
      }
    } else if (jobStatus.status === 'Failed') {
      notifiedRef.current = true;
      notify.error(jobStatus.errorMessage || "Import failed.");
    }
  }, [jobStatus, notify]);

  const handleDownloadTemplate = async () => {
    try {
      const blob = await downloadTemplateMutation.mutateAsync();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "customer_import_template.xlsx";
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      notify.success("Template downloaded successfully");
    } catch {
      notify.error("Failed to download template");
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const validTypes = [
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "application/vnd.ms-excel",
      ];
      if (!validTypes.includes(file.type)) {
        notify.error("Please select a valid Excel file (.xlsx or .xls)");
        return;
      }
      setSelectedFile(file);
      setJobId(null);
      notifiedRef.current = false;
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      notify.error("Please select a file to upload");
      return;
    }

    setIsUploading(true);
    notifiedRef.current = false;
    try {
      const result = await importMutation.mutateAsync(selectedFile);
      setJobId(result.jobId);
      notify.info("File uploaded — import is processing in the background.");
    } catch (error: unknown) {
      const message = getErrorMessage(error);
      notify.error(message || "Failed to upload file");
    } finally {
      setIsUploading(false);
    }
  };

  const handleReset = () => {
    setSelectedFile(null);
    setJobId(null);
    notifiedRef.current = false;
  };

  const handleClose = () => {
    handleReset();
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="gap-2">
          <Upload className="w-4 h-4" />
          Bulk Import
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Bulk Import Customers</DialogTitle>
          <DialogDescription>
            Import multiple customers from an Excel file
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Download Template Section */}
          <div className="border-b pb-6">
            <h3 className="font-semibold mb-3">Step 1: Download Template</h3>
            <Button
              variant="outline"
              onClick={handleDownloadTemplate}
              disabled={downloadTemplateMutation.isPending}
              className="gap-2"
            >
              <Download className="w-4 h-4" />
              {downloadTemplateMutation.isPending
                ? "Downloading..."
                : "Download Template"}
            </Button>
            <p className="text-sm text-gray-500 mt-2">
              Download the Excel template to see the expected format
            </p>
          </div>

          {/* File Upload Section */}
          <div className="border-b pb-6">
            <h3 className="font-semibold mb-3">Step 2: Select & Upload File</h3>
            <div className="flex flex-col gap-4">
              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileSelect}
                disabled={isUploading}
                className="block w-full text-sm text-gray-500
                  file:mr-4 file:py-2 file:px-4
                  file:rounded-md file:border-0
                  file:text-sm file:font-semibold
                  file:bg-blue-50 file:text-blue-700
                  hover:file:bg-blue-100
                  cursor-pointer"
              />
              {selectedFile && (
                <p className="text-sm text-gray-600">
                  Selected: <span className="font-medium">{selectedFile.name}</span>
                </p>
              )}
            </div>
          </div>

          {/* Upload Button Section */}
          {!jobId && (
            <div className="flex gap-3">
              <Button
                onClick={handleUpload}
                disabled={!selectedFile || isUploading}
              >
                {isUploading ? "Uploading..." : "Upload File"}
              </Button>
              {selectedFile && (
                <Button variant="outline" onClick={handleReset}>
                  Clear Selection
                </Button>
              )}
            </div>
          )}

          {/* Processing State */}
          {jobId && isProcessing && (
            <div className="space-y-4 border-t pt-6">
              <div className="flex items-center gap-3">
                <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
                <div>
                  <h3 className="font-semibold">
                    {jobStatus?.status === 'Pending' ? 'Queued...' : 'Importing customers...'}
                  </h3>
                  <p className="text-sm text-gray-500">
                    This runs in the background — you can close this dialog and come back later.
                  </p>
                </div>
              </div>
              <div className="h-2 w-full bg-gray-200 rounded-full overflow-hidden">
                <div className="h-full bg-blue-600 rounded-full animate-pulse" style={{ width: jobStatus?.status === 'Processing' ? '60%' : '15%' }} />
              </div>
            </div>
          )}

          {/* Results Section (terminal state) */}
          {jobId && isTerminal && jobStatus && (
            <div className="space-y-4 border-t pt-6">
              <h3 className="font-semibold">Import Results</h3>

              {/* Job failure */}
              {jobStatus.status === 'Failed' && (
                <Alert className="border-red-200 bg-red-50">
                  <AlertCircle className="h-4 w-4 text-red-600" />
                  <AlertDescription className="text-red-800">
                    {jobStatus.errorMessage || "Import failed."}
                  </AlertDescription>
                </Alert>
              )}

              {/* Summary */}
              {jobStatus.status === 'Completed' && (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <p className="text-sm text-gray-600">Total Rows</p>
                      <p className="text-2xl font-bold text-blue-600">
                        {jobStatus.totalRows}
                      </p>
                    </div>
                    <div className="bg-green-50 p-4 rounded-lg">
                      <p className="text-sm text-gray-600">Successful</p>
                      <p className="text-2xl font-bold text-green-600">
                        {jobStatus.successCount}
                      </p>
                    </div>
                    <div className="bg-red-50 p-4 rounded-lg">
                      <p className="text-sm text-gray-600">Skipped</p>
                      <p className="text-2xl font-bold text-red-600">
                        {jobStatus.failureCount}
                      </p>
                    </div>
                  </div>

                  {jobStatus.successCount > 0 && (
                    <Alert className="border-green-200 bg-green-50">
                      <CheckCircle2 className="h-4 w-4 text-green-600" />
                      <AlertDescription className="text-green-800">
                        {jobStatus.successCount} customer
                        {jobStatus.successCount !== 1 ? "s" : ""} imported
                        successfully
                      </AlertDescription>
                    </Alert>
                  )}

                  {/* Errors Section */}
                  {jobStatus.errors && jobStatus.errors.length > 0 && (
                    <div className="space-y-3">
                      <h4 className="font-semibold text-sm flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4 text-red-600" />
                        Import Errors ({jobStatus.errors.length} rows)
                      </h4>
                      <div className="bg-red-50 border border-red-200 rounded-lg p-4 max-h-64 overflow-y-auto space-y-3">
                        {jobStatus.errors.map((error, index) => (
                          <div key={index} className="text-sm">
                            <div className="font-medium text-red-900">
                              Row {error.rowNumber}: {error.customerName || error.email}
                            </div>
                            <ul className="list-disc list-inside text-red-700 ml-2 text-xs">
                              {error.errors.map((err, errIndex) => (
                                <li key={errIndex}>{err}</li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4">
                <Button onClick={handleReset} variant="outline">
                  Import Another File
                </Button>
                <Button onClick={handleClose}>Close</Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
